<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useFormat } from '../composables/useFormat.js'
import { useLocalization } from '@/composables/useLocalization'

const { __ } = useLocalization()

type PointRow = {
  id: number
  side: string
  side_label: string
  price: number
  status: string
  status_label: string
  mtf_label?: string | null
  url: string
}

const props = defineProps<{
  open: boolean
  label: string
  symbol: string
  lastPrice?: number | null
  longs: PointRow[]
  shorts: PointRow[]
}>()

const emit = defineEmits<{
  close: []
}>()

const { price } = useFormat()

const total = computed(() => props.longs.length + props.shorts.length)

function roleLabel(value?: string | null) {
  if (value === 'continue') return __('Continue')
  if (value === 'reverse') return __('Reverse')
  return value || ''
}

function roleKind(value?: string | null): 'continue' | 'reverse' | '' {
  if (value === 'continue' || value === 'reverse') return value
  return ''
}

function distance(row: PointRow) {
  if (props.lastPrice == null || !row.price) return null
  const pct = ((row.price - props.lastPrice) / props.lastPrice) * 100
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open) {
    event.preventDefault()
    emit('close')
  }
}

onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <Teleport to="body">
    <Transition name="board-modal">
      <div
        v-if="open"
        class="board-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="__('Main points')"
        @click.self="emit('close')"
      >
        <div class="board-modal__card" dir="ltr" @click.stop>
          <header class="board-modal__head">
            <div class="min-w-0">
              <p class="board-modal__kicker">{{ __('Main points') }}</p>
              <h3 class="board-modal__title">
                {{ label }}
                <span class="board-modal__sym">{{ symbol }}</span>
              </h3>
              <p v-if="lastPrice != null" class="board-modal__live">
                <span class="board-modal__live-dot" />
                {{ __('Live') }}
                <span class="board-modal__live-px">{{ price(lastPrice) }}</span>
              </p>
            </div>
            <button
              type="button"
              class="board-modal__close"
              :aria-label="__('Close')"
              @click="emit('close')"
            >
              ×
            </button>
          </header>

          <p v-if="!total" class="board-modal__empty">
            {{ __('No live main points on this coin.') }}
          </p>

          <div v-else class="board-modal__cols">
            <section class="board-modal__col board-modal__col--long">
              <h4 class="board-modal__col-title">
                <span class="board-modal__dot board-modal__dot--long" />
                {{ __('Long') }}
                <span class="board-modal__n">{{ longs.length }}</span>
              </h4>
              <p v-if="!longs.length" class="board-modal__none">{{ __('None') }}</p>
              <ul v-else class="board-modal__list">
                <li v-for="row in longs" :key="row.id">
                  <a :href="row.url" class="board-modal__row board-modal__row--long">
                    <span class="board-modal__px">{{ price(row.price) }}</span>
                    <span class="board-modal__meta">
                      <span class="board-modal__status">{{ __(row.status_label) }}</span>
                      <span v-if="distance(row)" class="board-modal__dist">{{ distance(row) }}</span>
                    </span>
                    <span
                      v-if="row.mtf_label"
                      class="board-modal__chip"
                      :class="roleKind(row.mtf_label) === 'reverse'
                        ? 'board-modal__chip--reverse'
                        : 'board-modal__chip--continue'"
                    >
                      {{ roleLabel(row.mtf_label) }}
                    </span>
                  </a>
                </li>
              </ul>
            </section>

            <section class="board-modal__col board-modal__col--short">
              <h4 class="board-modal__col-title">
                <span class="board-modal__dot board-modal__dot--short" />
                {{ __('Short') }}
                <span class="board-modal__n">{{ shorts.length }}</span>
              </h4>
              <p v-if="!shorts.length" class="board-modal__none">{{ __('None') }}</p>
              <ul v-else class="board-modal__list">
                <li v-for="row in shorts" :key="row.id">
                  <a :href="row.url" class="board-modal__row board-modal__row--short">
                    <span class="board-modal__px">{{ price(row.price) }}</span>
                    <span class="board-modal__meta">
                      <span class="board-modal__status">{{ __(row.status_label) }}</span>
                      <span v-if="distance(row)" class="board-modal__dist">{{ distance(row) }}</span>
                    </span>
                    <span
                      v-if="row.mtf_label"
                      class="board-modal__chip"
                      :class="roleKind(row.mtf_label) === 'reverse'
                        ? 'board-modal__chip--reverse'
                        : 'board-modal__chip--continue'"
                    >
                      {{ roleLabel(row.mtf_label) }}
                    </span>
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.board-modal {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(8, 10, 16, 0.78);
  backdrop-filter: blur(18px);
}
@media (max-width: 640px) {
  .board-modal {
    padding: 0.45rem;
    align-items: stretch;
  }
}
.board-modal__card {
  width: min(56rem, 100%);
  max-height: min(42rem, calc(100vh - 3rem));
  overflow: auto;
  border-radius: 1.15rem;
  background:
    radial-gradient(120% 80% at 0% 0%, rgba(38, 166, 154, 0.08), transparent 46%),
    radial-gradient(120% 80% at 100% 0%, rgba(239, 83, 80, 0.08), transparent 46%),
    #131722;
  border: 1px solid #363a45;
  color: #d1d4dc;
  box-shadow:
    0 28px 80px rgba(0, 0, 0, 0.62),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
}
@media (max-width: 640px) {
  .board-modal__card {
    max-height: none;
    height: 100%;
    border-radius: 1rem;
  }
}
.board-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.35rem 1.05rem;
  border-bottom: 1px solid #2a2e39;
  background: linear-gradient(180deg, #1e222d 0%, #181c27 100%);
}
.board-modal__kicker {
  margin: 0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #8b919d;
}
.board-modal__title {
  margin: 0.28rem 0 0;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: #f0f3fa;
  line-height: 1.1;
}
.board-modal__sym {
  margin-inline-start: 0.45rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  font-weight: 600;
  color: #8b919d;
}
.board-modal__live {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.5rem 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #8b919d;
}
.board-modal__live-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: #26a69a;
  box-shadow: 0 0 0 4px rgba(38, 166, 154, 0.16);
}
.board-modal__live-px {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #f0f3fa;
  font-size: 13px;
}
.board-modal__close {
  width: 2.15rem;
  height: 2.15rem;
  border-radius: 0.7rem;
  border: 1px solid #3d4250;
  background: #131722;
  color: #d1d4dc;
  font-size: 1.35rem;
  line-height: 1;
}
.board-modal__close:hover {
  background: #2a2e39;
  color: #fff;
}
.board-modal__empty,
.board-modal__none {
  margin: 0;
  padding: 1.7rem 1.35rem;
  text-align: center;
  font-size: 13px;
  color: #8b919d;
}
.board-modal__none {
  padding: 1rem 0.2rem;
  text-align: start;
}
.board-modal__cols {
  display: grid;
  gap: 1rem;
  padding: 1.1rem 1.2rem 1.25rem;
}
@media (min-width: 640px) {
  .board-modal__cols {
    grid-template-columns: 1fr 1fr;
  }
}
.board-modal__col {
  min-width: 0;
  border-radius: 1rem;
  padding: 0.85rem 0.8rem 0.9rem;
  border: 1px solid #2a2e39;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.board-modal__col--long {
  background: linear-gradient(180deg, rgba(38, 166, 154, 0.16) 0%, rgba(19, 23, 34, 0.55) 42%);
  border-color: rgba(38, 166, 154, 0.42);
}
.board-modal__col--short {
  background: linear-gradient(180deg, rgba(239, 83, 80, 0.16) 0%, rgba(19, 23, 34, 0.55) 42%);
  border-color: rgba(239, 83, 80, 0.42);
}
.board-modal__col-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.75rem;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.board-modal__col--long .board-modal__col-title {
  color: #4ecdc4;
}
.board-modal__col--short .board-modal__col-title {
  color: #ff8a80;
}
.board-modal__dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
}
.board-modal__dot--long {
  background: #26a69a;
  box-shadow: 0 0 0 4px rgba(38, 166, 154, 0.2), 0 0 12px rgba(38, 166, 154, 0.55);
}
.board-modal__dot--short {
  background: #ef5350;
  box-shadow: 0 0 0 4px rgba(239, 83, 80, 0.2), 0 0 12px rgba(239, 83, 80, 0.55);
}
.board-modal__n {
  margin-inline-start: auto;
  min-width: 1.4rem;
  height: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #d1d4dc;
  background: rgba(255, 255, 255, 0.06);
}
.board-modal__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.board-modal__row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border-radius: 0.85rem;
  padding: 0.85rem 0.9rem 0.8rem;
  text-decoration: none;
  color: inherit;
  background: #0e121b;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
}
.board-modal__row--long {
  border: 1px solid rgba(38, 166, 154, 0.28);
  box-shadow: inset 4px 0 0 #26a69a, 0 8px 22px rgba(0, 0, 0, 0.28);
}
.board-modal__row--short {
  border: 1px solid rgba(239, 83, 80, 0.28);
  box-shadow: inset 4px 0 0 #ef5350, 0 8px 22px rgba(0, 0, 0, 0.28);
}
.board-modal__row:hover {
  background: #1a2030;
}
.board-modal__px {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #f0f3fa;
}
.board-modal__row--long .board-modal__px {
  color: #4ecdc4;
}
.board-modal__row--short .board-modal__px {
  color: #ff8a80;
}
.board-modal__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.55rem;
  font-size: 12px;
}
.board-modal__status {
  color: #c5cad3;
}
.board-modal__dist {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #f0f3fa;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 0.08rem 0.45rem;
}
.board-modal__chip {
  align-self: flex-start;
  border-radius: 999px;
  padding: 0.22rem 0.62rem;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.board-modal__chip--continue {
  background: rgba(38, 166, 154, 0.22);
  color: #4ecdc4;
}
.board-modal__chip--reverse {
  background: rgba(239, 83, 80, 0.22);
  color: #ff8a80;
}
.board-modal-enter-active,
.board-modal-leave-active {
  transition: opacity 0.18s ease;
}
.board-modal-enter-from,
.board-modal-leave-to {
  opacity: 0;
}
</style>
